import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useMap } from 'react-leaflet'
import type L from 'leaflet'
import { ChevronDown, ChevronUp, Maximize2, Minimize2, X } from 'lucide-react'

/*
 * „Karte zuerst“: Bühne mit vollflächiger Karte und einklappbaren Panels darüber.
 *  - PC/Tablet: schwebende Seitenpanels links/rechts, je Panel einklappbar, Zustand in localStorage.
 *  - Handy (< 640 px): Karte vollflächig, Knopfleiste am unteren Kartenrand, Panels als Bottom-Sheet
 *    (Griff: Tippen/Ziehen = halb / voll / zu).
 *  - Vollbild-Knopf (Fullscreen-API, Fallback: feste Vollfläche).
 * Die Panels sind Geschwister des MapContainer (nicht darin): Klicks, Wischen und Mausrad erreichen die Karte nicht.
 * Kopie in apps/fuehrung, apps/leitstelle und apps/pilot (components/KartenPanel.tsx) – später nach packages/ui vereinheitlichen.
 * Z-Ordnung: alles innerhalb der Bühne (isolate), Pseudo-Vollbild z-[900] (unter Modalen >= 1000).
 */

const Q_HANDY = '(max-width: 639px)'
const Q_BREIT = '(min-width: 1024px)'

export function useKartenHandy(): boolean {
  const [handy, setHandy] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(Q_HANDY).matches : false))
  useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia(Q_HANDY)
    const f = () => setHandy(mq.matches)
    mq.addEventListener('change', f)
    return () => mq.removeEventListener('change', f)
  }, [])
  return handy
}

/** Merkt die Karteninstanz (innerhalb von MapContainer einhängen). */
export function KartenRef({ mapRef }: { mapRef: React.MutableRefObject<L.Map | null> }) {
  const map = useMap()
  useEffect(() => { mapRef.current = map; return () => { if (mapRef.current === map) mapRef.current = null } }, [map, mapRef])
  return null
}

/** Beendet ein laufendes natives Vollbild (Dialoge außerhalb der Bühne sind im Vollbild sonst unsichtbar). */
export async function vollbildVerlassen(): Promise<void> {
  const d = document as any
  if (!(d.fullscreenElement || d.webkitFullscreenElement)) return
  try { await (d.exitFullscreen?.() ?? d.webkitExitFullscreen?.()) } catch { /* ignorieren */ }
}

export interface KartenPanelDef {
  id: string
  titel: string
  icon: ReactNode
  seite: 'links' | 'rechts'
  /** Inhalt; als Funktion mit `einklappen` (schließt am Handy das Sheet, z. B. nachdem ein Symbol zum Setzen gewählt wurde) */
  inhalt: ReactNode | ((ctx: { einklappen: () => void }) => ReactNode)
  /** Kleine Zusatzangabe im Kopf (Anzahl o. Ä.) */
  badge?: string | number
  /** Standard auf dem PC (>= 1024 px), solange nichts gespeichert ist; Tablet/Handy starten zu */
  startOffen?: boolean
  /** Panel gar nicht anzeigen */
  verbergen?: boolean
  /** Ändert sich dieser Wert (und ist wahr), öffnet sich das Panel (z. B. Ergebnis einer Abfrage) */
  oeffnenBei?: unknown
}

type SheetHoehe = 'zu' | 'halb' | 'voll'

function lesen(key: string): boolean | null {
  try { const v = localStorage.getItem(key); return v === '1' ? true : v === '0' ? false : null } catch { return null }
}
function schreiben(key: string, offen: boolean) {
  try { localStorage.setItem(key, offen ? '1' : '0') } catch { /* ignorieren */ }
}

const KARTE_CLS = 'rounded-xl border border-dark-border bg-dark-bg-secondary/95 shadow-2xl backdrop-blur'
const KNOPF_CLS = 'flex h-11 w-11 items-center justify-center rounded-xl border border-dark-border bg-dark-bg-secondary/95 text-dark-text shadow-2xl backdrop-blur hover:bg-dark-bg-tertiary'

export function KartenBuehne({ seitenKey, panels, mapRef, children, className = '' }: {
  /** Eindeutiger Schlüssel der Seite für den gespeicherten Panel-Zustand */
  seitenKey: string
  panels: KartenPanelDef[]
  mapRef: React.MutableRefObject<L.Map | null>
  /** Karte (MapContainer) und weitere Kartenüberlagerungen */
  children: ReactNode
  className?: string
}) {
  const handy = useKartenHandy()
  const buehne = useRef<HTMLDivElement>(null)
  const sichtbar = panels.filter(p => !p.verbergen)

  /* ---- Zustand je Panel (PC/Tablet) ---- */
  const standard = (p: KartenPanelDef) => (typeof window !== 'undefined' && window.matchMedia && window.matchMedia(Q_BREIT).matches ? !!p.startOffen : false)
  const [offen, setOffen] = useState<Record<string, boolean>>(() => {
    const o: Record<string, boolean> = {}
    for (const p of panels) o[p.id] = lesen(`kartenpanel:${seitenKey}:${p.id}`) ?? standard(p)
    return o
  })
  const setzeOffen = useCallback((id: string, wert: boolean) => {
    setOffen(o => ({ ...o, [id]: wert }))
    schreiben(`kartenpanel:${seitenKey}:${id}`, wert)
  }, [seitenKey])

  /* ---- Sheet (Handy) ---- */
  const [sheet, setSheet] = useState<SheetHoehe>('zu')
  const [sheetPanel, setSheetPanel] = useState<string | null>(null)
  const aktivPanel = sichtbar.find(p => p.id === sheetPanel) ?? sichtbar[0]

  /* ---- Panel öffnet sich auf Anforderung ---- */
  const letzteSignale = useRef<Record<string, unknown>>({})
  useEffect(() => {
    for (const p of panels) {
      if (p.oeffnenBei === letzteSignale.current[p.id]) continue
      letzteSignale.current[p.id] = p.oeffnenBei
      if (!p.oeffnenBei) continue
      if (handy) { setSheetPanel(p.id); setSheet(s => (s === 'zu' ? 'halb' : s)) }
      else setzeOffen(p.id, true)
    }
  }, [panels, handy, setzeOffen])

  /* ---- Kartengröße nachführen ---- */
  const invalidiere = useCallback(() => { mapRef.current?.invalidateSize() }, [mapRef])
  useEffect(() => {
    const el = buehne.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => invalidiere())
    ro.observe(el)
    return () => ro.disconnect()
  }, [invalidiere])
  useLayoutEffect(() => { invalidiere() }, [offen, sheet, handy, invalidiere])

  /* ---- Vollbild ---- */
  const [vollbild, setVollbild] = useState(false)   // natives Vollbild aktiv
  const [pseudo, setPseudo] = useState(false)       // Fallback ohne Fullscreen-API (z. B. iPhone-Safari)
  useEffect(() => {
    const f = () => {
      const d = document as any
      setVollbild(!!(d.fullscreenElement || d.webkitFullscreenElement) && (d.fullscreenElement || d.webkitFullscreenElement) === buehne.current)
      setTimeout(invalidiere, 100)
    }
    document.addEventListener('fullscreenchange', f)
    document.addEventListener('webkitfullscreenchange', f)
    return () => { document.removeEventListener('fullscreenchange', f); document.removeEventListener('webkitfullscreenchange', f) }
  }, [invalidiere])
  useEffect(() => {
    if (!pseudo) return
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setPseudo(false) }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [pseudo])
  useEffect(() => { setTimeout(invalidiere, 100) }, [pseudo, invalidiere])

  const vollbildUmschalten = async () => {
    const el = buehne.current as any
    const d = document as any
    if (pseudo) { setPseudo(false); return }
    if (vollbild) { try { await (d.exitFullscreen?.() ?? d.webkitExitFullscreen?.()) } catch { /* ignorieren */ } return }
    const fn = el?.requestFullscreen ?? el?.webkitRequestFullscreen
    if (!fn) { setPseudo(true); return }
    try { await fn.call(el) } catch { setPseudo(true) }
  }
  const imVollbild = vollbild || pseudo
  const vollbildKnopf = (
    <button type="button" onClick={vollbildUmschalten} className={KNOPF_CLS}
      aria-label={imVollbild ? 'Vollbild beenden' : 'Vollbild'} title={imVollbild ? 'Vollbild beenden' : 'Vollbild'}>
      {imVollbild ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
    </button>
  )

  const inhaltVon = (p: KartenPanelDef) => typeof p.inhalt === 'function'
    ? p.inhalt({ einklappen: () => { if (handy) setSheet('zu') } })
    : p.inhalt

  /* ---- Sheet-Griff: Tippen wechselt halb/voll, Ziehen nach oben/unten ändert die Höhe ---- */
  const griff = useRef<{ y: number } | null>(null)
  const hoeher = (s: SheetHoehe): SheetHoehe => (s === 'zu' ? 'halb' : 'voll')
  const niedriger = (s: SheetHoehe): SheetHoehe => (s === 'voll' ? 'halb' : 'zu')

  const seitenspalte = (seite: 'links' | 'rechts') => {
    const liste = sichtbar.filter(p => p.seite === seite)
    if (!liste.length && seite === 'links') return null
    return (
      <div className={`pointer-events-none absolute top-3 z-30 flex max-h-[calc(100%-1.5rem)] flex-col gap-2 overflow-y-auto overscroll-contain ${seite === 'links' ? 'left-3 bottom-3 w-[min(300px,calc(50%-1rem))] min-w-[250px]' : 'right-3 bottom-24 w-[min(300px,calc(50%-1rem))] min-w-[250px] items-end'}`}>
        {seite === 'rechts' && <div className="pointer-events-auto shrink-0">{vollbildKnopf}</div>}
        {liste.map(p => {
          const o = offen[p.id]
          return (
            <section key={p.id} className={`pointer-events-auto w-full shrink-0 ${KARTE_CLS}`} aria-label={p.titel}>
              <button type="button" aria-expanded={o}
                onClick={() => setzeOffen(p.id, !o)}
                className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm font-semibold text-dark-text hover:text-primary-400">
                <span className="text-primary-400">{p.icon}</span>
                <span className="flex-1 truncate">{p.titel}</span>
                {p.badge !== undefined && p.badge !== '' && <span className="rounded-full bg-dark-bg-tertiary px-2 py-0.5 text-xs font-normal text-dark-text-muted">{p.badge}</span>}
                {o ? <ChevronUp className="h-4 w-4 text-dark-text-subtle" /> : <ChevronDown className="h-4 w-4 text-dark-text-subtle" />}
              </button>
              {o && <div className="max-h-[min(55dvh,520px)] overflow-y-auto overscroll-contain border-t border-dark-border p-3">{inhaltVon(p)}</div>}
            </section>
          )
        })}
      </div>
    )
  }

  return (
    <div ref={buehne}
      className={`isolate overflow-hidden bg-dark-bg ${pseudo ? 'fixed inset-0 z-[900] h-full w-full' : 'relative h-full w-full rounded-xl'} ${className}`}>
      {children}

      {!handy && (
        <>
          {seitenspalte('links')}
          {seitenspalte('rechts')}
        </>
      )}

      {handy && (
        <>
          {sheet === 'zu' && (
            <>
              <div className="absolute right-3 top-3 z-30">{vollbildKnopf}</div>
              <div className="pointer-events-none absolute bottom-3 left-3 z-30 max-w-[calc(100%-5.5rem)]">
                <div className="pointer-events-auto flex gap-2 overflow-x-auto">
                  {sichtbar.map(p => (
                    <button key={p.id} type="button" onClick={() => { setSheetPanel(p.id); setSheet('halb') }}
                      className={`flex min-h-[44px] shrink-0 items-center gap-1.5 px-3 text-xs font-semibold text-dark-text ${KARTE_CLS}`}>
                      <span className="text-primary-400">{p.icon}</span>{p.titel}
                      {p.badge !== undefined && p.badge !== '' && <span className="rounded-full bg-dark-bg-tertiary px-1.5 text-[11px] font-normal text-dark-text-muted">{p.badge}</span>}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {sheet !== 'zu' && aktivPanel && (
            <div className={`absolute inset-x-0 bottom-0 z-[36] flex flex-col rounded-t-2xl border border-b-0 border-dark-border bg-dark-bg-secondary shadow-2xl ${sheet === 'voll' ? 'h-[88%]' : 'h-[46%]'}`}
              role="dialog" aria-label={aktivPanel.titel}>
              <div className="flex shrink-0 flex-col">
                <button type="button" aria-label="Höhe ändern" style={{ touchAction: 'none' }}
                  className="flex h-6 w-full cursor-grab items-center justify-center"
                  onPointerDown={e => { griff.current = { y: e.clientY }; (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId) }}
                  onPointerUp={e => {
                    const g = griff.current; griff.current = null
                    if (!g) return
                    const dy = e.clientY - g.y
                    if (dy < -30) setSheet(hoeher)
                    else if (dy > 30) setSheet(niedriger)
                    else setSheet(s => (s === 'halb' ? 'voll' : 'halb'))
                  }}
                  onPointerCancel={() => { griff.current = null }}>
                  <span className="h-1.5 w-12 rounded-full bg-dark-border" />
                </button>
                <div className="flex items-center gap-1 px-2 pb-1">
                  <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
                    {sichtbar.map(p => (
                      <button key={p.id} type="button" aria-pressed={p.id === aktivPanel.id} onClick={() => setSheetPanel(p.id)}
                        className={`flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold ${p.id === aktivPanel.id ? 'bg-primary-500/20 text-dark-text' : 'text-dark-text-muted'}`}>
                        <span className="text-primary-400">{p.icon}</span>{p.titel}
                        {p.badge !== undefined && p.badge !== '' && <span className="rounded-full bg-dark-bg-tertiary px-1.5 text-[11px] font-normal">{p.badge}</span>}
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={() => setSheet('zu')} aria-label="Schließen"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-dark-border p-3 pb-4">{inhaltVon(aktivPanel)}</div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
